import { RequirementLeafType } from "../student-engagement/trails/trail.api";

export interface AcademicLeafLabel {
    leafType?: RequirementLeafType;
    courseId?: number;
    courseCode?: string;
    courseTitle?: string;
    minGrade?: string;
    isCorequisite?: boolean;
    disallowPassNoPass?: boolean;
    isProhibited?: boolean;
    subject?: string;
    levelMin?: number;
    levelMax?: number;
    externalCode?: string;
    requiredValue?: string;
    minNumericValue?: number;
}

export interface AcademicCourseLabel {
    courseId: number;
    courseCode: string;
    title?: string;
}

/** Course, rule, and fact text for one requirement leaf. The tree renders this from its courses input. */
export function academicLeafDetail(leaf: AcademicLeafLabel, courses: AcademicCourseLabel[]): string | undefined {
    switch (leaf.leafType) {
        case "COURSE":
            return joinDetail([
                courseTitle(leaf, courses),
                leaf.minGrade ? `Minimum grade: ${leaf.minGrade}` : undefined,
                leaf.isCorequisite ? "Corequisite" : undefined,
                leaf.disallowPassNoPass ? "Cannot be taken pass/no-pass" : undefined,
                leaf.isProhibited ? "Prohibited" : undefined,
            ]);
        case "COURSE_RULE":
            return joinDetail([
                leaf.subject ? `Subject: ${leaf.subject}` : undefined,
                levelDetail(leaf),
                leaf.minGrade ? `Minimum grade: ${leaf.minGrade}` : undefined,
                leaf.disallowPassNoPass ? "Cannot be taken pass/no-pass" : undefined,
            ]);
        case "EXTERNAL":
            return joinDetail([
                leaf.externalCode,
                leaf.requiredValue,
                leaf.minNumericValue != null ? `Minimum value: ${leaf.minNumericValue}` : undefined,
            ]);
        default:
            return undefined;
    }
}

function courseTitle(leaf: AcademicLeafLabel, courses: AcademicCourseLabel[]): string | undefined {
    const listed = leaf.courseId == null ? undefined : courses.find((item) => item.courseId === leaf.courseId);
    const courseCode = listed?.courseCode ?? leaf.courseCode;
    const title = listed?.title ?? leaf.courseTitle;
    if (!courseCode) return undefined;
    return title ? `${courseCode} — ${title}` : courseCode;
}

function levelDetail(leaf: AcademicLeafLabel): string | undefined {
    if (leaf.levelMin == null && leaf.levelMax == null) return undefined;
    return `Level: ${leaf.levelMin ?? "—"}–${leaf.levelMax ?? "—"}`;
}

function joinDetail(parts: Array<string | undefined>): string | undefined {
    const present = parts.filter((part): part is string => !!part?.trim());
    return present.length ? present.join(" · ") : undefined;
}
